 
import BigNumber from 'bignumber.js';
import { createSelector } from '@reduxjs/toolkit';
import { SupportedChainId } from '@orionprotocol/sdk';
import { i18n } from '@lingui/core';
import { getNonNegativeBN } from '@orionprotocol/utils';

import { RootStateType } from '../store';
import { BalancesState } from '../slices';

export const getIsLogged = (state: RootStateType) =>
  state.user.wallet?.address !== undefined;
export const getWalletType = (state: RootStateType) =>
  state.user.wallet.walletType;
export const getWalletConnectWalletName = (state: RootStateType) =>
  state.user.wallet.walletConnectWalletName;
export const getWalletAddress = (state: RootStateType) =>
  state.user.wallet.address;
export const getWalletChainId = (state: RootStateType) =>
  state.user.wallet.walletChainId;
export const getIsWalletConnecting = (state: RootStateType) =>
  state.user.wallet.isConnecting;

const calculateBalancesObject = (balances: BalancesState | undefined) => {
  if (balances) {
    const { wallet, allowance, contract, locked } = balances;
    const available: Partial<Record<string, string>> = {};
    const imaginary: Partial<Record<string, string>> = {};

    Object.keys(wallet).forEach((currency) => {
      const currencyWalletBalance = wallet[currency];
      const currencyAllowance = allowance[currency];

      if (currencyAllowance === undefined) return;
      if (currencyWalletBalance === undefined)
        throw new Error(
          i18n._('Wallet currency {currency} is not defined', {
            currency,
          }),
        );

      const walletValueBN = new BigNumber(currencyWalletBalance);

      const walletOrAllowanceValueBN = BigNumber.min(
        currencyAllowance,
        walletValueBN,
      );

      const contractValueBN = new BigNumber(contract[currency] ?? 0);
      const swapThroughOrionPoolLockedBN = new BigNumber(
        locked.local.swapThroughOrionPool[currency] ?? 0,
      );
      const aggregatorLockedBN = new BigNumber(
        locked.external.aggregator[currency] ?? 0,
      );
      const pendingAtomicLockedBN = new BigNumber(
        locked.local.atomic.pending[currency] ?? 0,
      );

      const lockedValueBN = swapThroughOrionPoolLockedBN
        .plus(aggregatorLockedBN)
        .plus(pendingAtomicLockedBN);

      // Formula: AvailableBalance = min(WalletBalance,AllowanceBalance)  + ContractBalance - LockedBalance
      // LockedBalance = aggregatorLocked + localLocked + atomic.pending
      available[currency] = getNonNegativeBN(
        walletOrAllowanceValueBN.plus(contractValueBN).minus(lockedValueBN),
      ).toString();

      // Formula: ImaginaryBalance = WalletBalance + ContractBalance - LockedBalance
      imaginary[currency] = getNonNegativeBN(
        walletValueBN.plus(contractValueBN).minus(lockedValueBN),
      ).toString();
    });

    return {
      wallet,
      allowance,
      contract,
      locked,
      available,
      imaginary,
    };
  }

  return null;
};

export const getCurrentBalances = ({ user }: RootStateType) => user.balances;
export const getCurrentChainBalances = createSelector(
  ({ app, user }: RootStateType) => user.balances[app.chainId],
  calculateBalancesObject,
);

export const getBalancesByChain = createSelector(
  [
    ({ user }: RootStateType) => user.balances,
    (_, chainId: SupportedChainId | undefined) => chainId,
  ],
  (balances, chainId) =>
    chainId ? calculateBalancesObject(balances[chainId]) : null,
);

// export const getCurrentNetworkOrderHistory = ({ user, app }: RootStateType) =>
//   user.orders[app.chainId];
// export const getIsOrdersLoaded = (store: RootStateType) =>
//   store.user.isOrdersLoaded;
